import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getFocusedFilterForTable from 'mini-mail-list-store/lib/utils/getFocusedFilterForTable';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import isFocusedInboxEnabled from 'mini-mail-focused-inbox-config/lib/isFocusedInboxEnabled';
import type MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import selectFocusedPivot from 'mini-mail-list-actions/lib/actions/selectFocusedPivot';
import signalReadingPaneClosed from 'mini-mail-reading-pane-store/lib/actions/signalReadingPaneClosed';
import { getDefaultFolderToLoad } from 'mini-mail-folder-store/lib/utils/getDefaultFolderToLoad';
import { getQueryStringParameter, removeQuery } from 'owa-querystring';
import { orchestrator } from 'satcheljs';
import selectFolder, {
    selectFolderInternal,
} from 'mini-mail-folder-actions/lib/actions/selectFolder';

const FOCUSED_OTHER_FILTER_QUERY_PARAM = 'focusedFilter';
const OTHER_FILTER = 'Other';

/**
 * Attempts to hydrate list view for given folder name or id.
 * This is attempted before loading conversation/item Reading Pane so as to load the conversation/item in List view as well.
 * @param folderNameOrId input folder name or id.
 *
 * Note: This has below limitations, but we are okay to live with as there is no business requirement as of now:
 * - Can load RP for first 25 mails only in a folder as they are first loaded in list view store.
 * - When Focused-Other setting is enabled, Other inbox RP can only be loaded if we have query param 'focusedFilter=Other'
 *   to indicate that the mail is from Other inbox.
 * More info on WI: #147708.
 */
export const tryHydratingListView = async (folderNameOrId: string): Promise<void> => {
    const defaultTableView = getSelectedTableView();

    // Non-inbox case
    if (folderNameOrId !== getDefaultFolderToLoad()) {
        removeFocusedOtherFilterFromQueryString();
        return selectFolder(folderNameOrId);
    }

    const rowFocusedOtherFilter = getFocusedOtherFilterFromQueryStringWithFallback();

    const isCurrentFocusedOtherFilterSameAsQueryParam =
        getFocusedFilterForTable(defaultTableView) === rowFocusedOtherFilter;

    if (!isFocusedInboxEnabled() || isCurrentFocusedOtherFilterSameAsQueryParam) {
        removeFocusedOtherFilterFromQueryString();
        // Refresh table.
        return selectFolderInternal(folderNameOrId, {
            shouldWaitForFreshData: true,
            shouldPrefetchFirstNRows: false,
        });
    }

    //  Change focused-other filter otherwise
    return selectFocusedPivot(rowFocusedOtherFilter, null /* sourceElement */);
};

const getFocusedOtherFilterFromQueryStringWithFallback = (): FocusedViewFilter => {
    if (getQueryStringParameter(FOCUSED_OTHER_FILTER_QUERY_PARAM) === OTHER_FILTER) {
        return 1;
    }
    return 0;
};

const removeFocusedOtherFilterFromQueryString = () => {
    const newSearch = removeQuery(location.search, FOCUSED_OTHER_FILTER_QUERY_PARAM);
    history.replaceState(history.state, '', `${location.pathname}${newSearch}`);
};

/**
 * Business logic to ensure user lands in Focused Inbox on closing reading pane of other inbox conversation/item,
 * in case the conversation/item reading pane was loaded directly, e.g. from push notifications, etc.
 */
orchestrator(signalReadingPaneClosed, () => {
    // Not using 'getQueryStringParameter' utility here as that uses cached value which could be stale.
    if (!location.search.includes(`${FOCUSED_OTHER_FILTER_QUERY_PARAM}=`)) {
        return;
    }

    removeFocusedOtherFilterFromQueryString();

    const mailTableQuery = getSelectedTableView().tableQuery as MailFolderTableQuery;
    const isRowFromOtherInbox =
        mailTableQuery.folderId === folderNameToId(getDefaultFolderToLoad()) &&
        mailTableQuery.focusedViewFilter === 1;

    if (isRowFromOtherInbox) {
        selectFocusedPivot(0, null /* sourceElement */);
    }
});
